import axiosApiInstance from "../../helpers/interceptor";
import { Client, ClientExtraConfig } from "./clients/clientAPI";

const getUserData = "users/getUser";
const getOtherUserData = "users/getUserById";
const getRoleOptions = "users/predeterminedRoles";
const getCurrentRoles = "users/currentRoles";
const getFilteredUsers = "users/filteredUsers";
const createUser = "users/create";
const createMultipleUsers = "users/createMultipleUsers";
const restorePassword = "users/restorePassword";
const resetOwnPassword = "users/resetOwnPassword";
const changeOwnInfo = "users/editOwnInfo";
const changeUserInfo = "users/editUserInfo";
const toggleUserStatus = "users/toggleUserStatus";
const toggleMultipleUserStatusPath = "users/toggleMultipleUserStatus";
const deleteUser = "users/deleteUser";
const deleteMultipleUsersPath = "users/deleteMultipleUsers";
const changeUserRoles = "users/changeUserRoles";
const changeOwnRoles = "users/changeOwnRoles";
const getRoleProfilesPath = "users/getRoleProfiles";
const createRoleProfilePath = "users/createRoleProfile";
const updateRoleProfilePath = "users/updateRoleProfile";
const deleteRoleProfilePath = (id: string) => `users/deleteRoleProfile/${id}`;
let getExtraConfig = "clientData/getCustomTimeout";
let changeExtraConfig = "clientData/updateCustomTimeout";
const getAuthConfigPath = "clientData/getAuthConfig";
const updateAuthConfigPath = "clientData/updateAuthConfig";

export const getUserDataAPI = async (): Promise<UserData | null> => {
  try {
    let userDataResponse = await axiosApiInstance.get<UserData>(getUserData);
    return userDataResponse.data;
  } catch {
    return null;
  }
};

export const getOtherUserDataAPI = async (
  id: string
): Promise<UserData | null> => {
  try {
    let userDataResponse = await axiosApiInstance.get<UserData>(
      `${getOtherUserData}/${id}`
    );
    return userDataResponse.data;
  } catch {
    return null;
  }
};

export const getRoleOptionsAPI = async (): Promise<PredeterminedRoles[]> => {
  try {
    let rolesResponse = await axiosApiInstance.get<PredeterminedRoles[]>(
      getRoleOptions
    );
    return rolesResponse.data;
  } catch {
    return [];
  }
};

export const getCurrentRolesAPI = async (): Promise<string[]> => {
  try {
    let rolesResponse = await axiosApiInstance.get<string[]>(getCurrentRoles);
    return rolesResponse.data;
  } catch {
    return [];
  }
};

export const createUserAPI = async (req: CreateUserRequest) => {
  try {
    let response = await axiosApiInstance.post<CreateUserResponse>(
      createUser,
      req
    );
    return { data: response.data, error: "" };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { data: null, error: "error de servidor" };
    } else if (e.response.status === 400) {
      if (e.response.data && e.response.data === "user already exists") {
        return {
          data: null,
          error: "El usuario con el mismo correo ya existe",
        };
      }
      return { data: null, error: "Un usuario con el mismo correo ya existe" };
    } else if (e.response.status === 401) {
      return {
        data: null,
        error: "No tienes permisos para realizar esta acción",
      };
    } else {
      return { data: null, error: "error inesperado" };
    }
  }
};

export const createMultipleUsersAPI = async (req: AddMultipleUsersReq) => {
  try {
    let response = await axiosApiInstance.post<AddMultipleUsersResponse>(
      createMultipleUsers,
      req
    );
    return { data: response.data, error: "" };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { data: null, error: "error de servidor" };
    } else if (e.response.status === 400) {
      return { data: null, error: "Hubo un error al crear los usuarios" };
    } else if (e.response.status === 401) {
      return {
        data: null,
        error: "No tienes permisos para realizar esta acción",
      };
    } else {
      return { data: null, error: "error inesperado" };
    }
  }
};

export const changeOwnInfoAPI = async (
  req: ChangeInfoRequest
): Promise<{ error: string; userData: UserData | undefined }> => {
  try {
    let response = await axiosApiInstance.post<UserData>(changeOwnInfo, req);
    return { userData: response.data, error: "" };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return {
        userData: undefined,
        error: "No tiene permisos para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return {
        userData: undefined,
        error: "Error con servidor. Intenta de nuevo",
      };
    } else {
      return {
        userData: undefined,
        error: "Error inesperado. Intenta de nuevo",
      };
    }
  }
};

export const changeUserInfoAPI = async (
  id: string,
  req: ChangeInfoRequest
): Promise<{ error: string; userData: UserData | undefined }> => {
  try {
    let response = await axiosApiInstance.post<UserData>(
      `${changeUserInfo}/${id}`,
      req
    );
    return { userData: response.data, error: "" };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return {
        userData: undefined,
        error: "No tiene permisos para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return {
        userData: undefined,
        error: "Error con servidor. Intenta de nuevo",
      };
    } else {
      return {
        userData: undefined,
        error: "Error inesperado. Intenta de nuevo",
      };
    }
  }
};

export const toggleUserStatusAPI = async (
  id: string
): Promise<{ error: string; userData: UserData | null }> => {
  try {
    let response = await axiosApiInstance.get<UserData>(
      `${toggleUserStatus}/${id}`
    );
    return { userData: response.data, error: "" };
  } catch {
    return { error: "error", userData: null };
  }
};

export const toggleMultipleUserStatusAPI = async (
  req: ChangeMultipleUsersReq
): Promise<{ error: string; result: boolean }> => {
  try {
    let response = await axiosApiInstance.post<boolean>(
      toggleMultipleUserStatusPath,
      req
    );
    if (response.data) {
      return { result: response.data, error: "" };
    } else {
      return {
        result: response.data,
        error: "Hubo un error al cambiar los estados",
      };
    }
  } catch {
    return { error: "Se generó un erro al cambiar los estados", result: false };
  }
};

export const deleteUserAPI = async (id: string) => {
  try {
    let response = await axiosApiInstance.delete<boolean>(
      `${deleteUser}/${id}`
    );
    return { userData: response.data, error: "" };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return {
        userData: undefined,
        error: "No tiene permisos para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return {
        userData: undefined,
        error: "Error con servidor. Intenta de nuevo",
      };
    } else {
      return {
        userData: undefined,
        error: "Error inesperado. Intenta de nuevo",
      };
    }
  }
};

export const deleteMultipleUserAPI = async (req: ChangeMultipleUsersReq) => {
  try {
    let response = await axiosApiInstance.post<boolean>(
      deleteMultipleUsersPath,
      req
    );
    return { result: response.data, error: "" };
  } catch {
    return { error: "error", result: false };
  }
};

export const restorePasswordAPI = async (id: string, newPassword?: string) => {
  try {
    let userDataResponse =
      await axiosApiInstance.post<ResetBackofficePasswordResponse>(
        `${restorePassword}/${id}`,
        { newPassword }
      );
    return { result: userDataResponse.data, error: undefined };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return {
        result: undefined,
        error: "No tiene permisos para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return {
        result: undefined,
        error: "Error con servidor. Intenta de nuevo",
      };
    } else {
      return {
        result: undefined,
        error: "Error inesperado. Intenta de nuevo",
      };
    }
  }
};

export const resetOwnPasswordAPI = async (req: ChangeOwnPasswordReq) => {
  try {
    let resetOwnPasswordRes = await axiosApiInstance.post<boolean>(
      `${resetOwnPassword}`,
      req
    );
    return { result: resetOwnPasswordRes.data, message: "" };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return { result: false, message: "Contraseña incorrecta" };
    } else if (e.response && e.response.status === 500) {
      return { result: false, message: "Error con servidor. Intenta de nuevo" };
    } else {
      return { result: false, message: "Error inesperado. Intenta de nuevo" };
    }
  }
};

export const changeUserRolesAPI = async (req: ChangeUserRolesReq) => {
  try {
    let result = await axiosApiInstance.post<UserData>(
      `${changeUserRoles}`,
      req
    );
    return { result: result.data, message: "" };
  } catch (e: any) {
    if (e.response && e.response.status === 401) {
      return {
        result: null,
        message: "No tiene permiso para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return { result: null, message: "Error con servidor. Intenta de nuevo" };
    } else {
      return { result: null, message: "Error inesperado. Intenta de nuevo" };
    }
  }
};

export const changeOwnRolesAPI = async (req: ChangeUserRolesReq) => {
  try {
    let result = await axiosApiInstance.post<UserData>(
      `${changeOwnRoles}`,
      req
    );
    return { result: result.data, message: "" };
  } catch (e: any) {
    if (
      e.response &&
      (e.response.status === 401 || e.response.status === 403)
    ) {
      return {
        result: null,
        message: "No tiene permiso para realizar esta acción",
      };
    } else if (e.response && e.response.status === 500) {
      return { result: null, message: "Error con servidor. Intenta de nuevo" };
    } else {
      return { result: null, message: "Error inesperado. Intenta de nuevo" };
    }
  }
};

export const getFilteredUsersAPI = async (
  request: FilteredUsersRequest
): Promise<{
  error: string;
  filteredUsers: FilteredUsersResponse | null;
}> => {
  try {
    let filteredUsers = await axiosApiInstance.post(getFilteredUsers, request);
    return {
      error: "",
      filteredUsers: {
        ...filteredUsers.data,
        currentPage: request.page,
      },
    };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { filteredUsers: null, error: e.response.data };
    } else if (e.response.status === 404) {
      return { filteredUsers: null, error: e.response.data };
    } else {
      return { filteredUsers: null, error: e.response.data };
    }
  }
};

export const getAllUsersAPI = async (): Promise<UserData[] | null> => {
  try {
    let users = await axiosApiInstance.get<UserData[]>("users/allUsers");
    return users.data;
  } catch (e: any) {
    if (e.response.status === 500) {
      return null;
    } else if (e.response.status === 404) {
      return null;
    } else {
      return null;
    }
  }
};

export const getFilteredEventsAPI = async (
  request: GetFilteredEventsRequest
): Promise<{
  error: string;
  filteredEvents: FilteredEventsResponse | null;
}> => {
  try {
    let filteredEvents = await axiosApiInstance.post(
      "history/getFilteredEvents",
      request
    );
    return {
      error: "",
      filteredEvents: {
        ...filteredEvents.data,
        currentPage: request.page,
      },
    };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { filteredEvents: null, error: e.response.data };
    } else if (e.response.status === 404) {
      return { filteredEvents: null, error: e.response.data };
    } else {
      return { filteredEvents: null, error: e.response.data };
    }
  }
};

export const getRoleProfilesAPI = async (): Promise<{
  error: string;
  roleProfiles: RoleProfile[];
}> => {
  try {
    const roleProfiles = await axiosApiInstance.get<RoleProfile[]>(
      `${getRoleProfilesPath}`
    );
    return {
      error: "",
      roleProfiles: roleProfiles.data,
    };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { roleProfiles: [], error: e.response.data };
    } else if (e.response.status === 404) {
      return { roleProfiles: [], error: e.response.data };
    } else {
      return { roleProfiles: [], error: e.response.data };
    }
  }
};

export const createRoleProfileAPI = async (
  req: RoleProfile
): Promise<{ error: string; roleProfile: RoleProfile | null }> => {
  try {
    const roleProfile = await axiosApiInstance.post<RoleProfile>(
      `${createRoleProfilePath}`,
      req
    );
    return { error: "", roleProfile: roleProfile.data };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { roleProfile: null, error: "error de servidor" };
    } else if (e.response.status === 401) {
      return {
        roleProfile: null,
        error: "No tiene permiso para realizar esta acción",
      };
    } else if (e.response.status === 400) {
      switch (e.response.data) {
        case "Roles empty":
          return {
            roleProfile: null,
            error: "No se puede crear un rol sin permisos",
          };
        case "Name empty":
          return { roleProfile: null, error: "El nombre es requerido" };
        case "Profile with the same name already exists":
          return {
            roleProfile: null,
            error: "Ya existe un rol con ese nombre",
          };
        default:
          return { roleProfile: null, error: "Error inesperado" };
      }
    } else {
      return { roleProfile: null, error: "Error inesperado" };
    }
  }
};

export const updateRoleProfileAPI = async (
  req: RoleProfile
): Promise<{ error: string; roleProfile: RoleProfile | null }> => {
  try {
    const roleProfile = await axiosApiInstance.post<RoleProfile>(
      `${updateRoleProfilePath}`,
      req
    );
    return { error: "", roleProfile: roleProfile.data };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { roleProfile: null, error: "error de servidor" };
    } else if (e.response.status === 401) {
      return {
        roleProfile: null,
        error: "No tiene permiso para realizar esta acción",
      };
    } else if (e.response.status === 400) {
      switch (e.response.data) {
        case "Roles empty":
          return {
            roleProfile: null,
            error: "No se puede crear un rol sin permisos",
          };
        case "Id empty":
          return { roleProfile: null, error: "El id es requerido" };
        case "Name empty":
          return { roleProfile: null, error: "El nombre es requerido" };
        case "Profile with the same name already exists":
          return {
            roleProfile: null,
            error: "Ya existe un rol con ese nombre",
          };
        default:
          return { roleProfile: null, error: "Error inesperado" };
      }
    } else {
      return { roleProfile: null, error: "Error inesperado" };
    }
  }
};

export const deleteRoleProfileAPI = async (
  id: string
): Promise<{ error: string; result: boolean }> => {
  try {
    const result = await axiosApiInstance.delete<boolean>(
      `${deleteRoleProfilePath(id)}`
    );
    return { error: "", result: result.data };
  } catch (e: any) {
    if (e.response.status === 500) {
      return { result: false, error: "error de servidor" };
    } else if (e.response.status === 401) {
      return {
        result: false,
        error: "No tiene permiso para realizar estaacción",
      };
    } else if (e.response.status === 400) {
      return { result: false, error: "Error inesperado" };
    } else if (e.response.status === 404) {
      return { result: false, error: "Rol no encontrado" };
    } else {
      return { result: false, error: "Error inesperado" };
    }
  }
};

export const getExtraConfigAPI = async (): Promise<{
  error: string;
  extraConfig: ClientExtraConfig | null;
}> => {
  try {
    let extraConfig = await axiosApiInstance.get<ClientExtraConfig>(
      getExtraConfig
    );
    if (extraConfig.status === 200) {
      return {
        error: "",
        extraConfig: extraConfig.data,
      };
    } else {
      return {
        error: "Error inesperado",
        extraConfig: null,
      };
    }
  } catch (e: any) {
    return {
      error: "Error inesperado",
      extraConfig: null,
    };
  }
};

export const changeExtraConfigAPI = async (
  req: ClientExtraConfig
): Promise<{
  error: string;
  extraConfig: ClientExtraConfig | null;
}> => {
  try {
    let extraConfig = await axiosApiInstance.post<ClientExtraConfig>(
      changeExtraConfig,
      req
    );
    if (extraConfig.status === 200) {
      return {
        error: "",
        extraConfig: extraConfig.data,
      };
    } else {
      return {
        error: "Error inesperado",
        extraConfig: null,
      };
    }
  } catch (e: any) {
    return {
      error: "Error inesperado",
      extraConfig: null,
    };
  }
};

export const getAuthConfigAPI = async (): Promise<{
  error: string;
  authConfig: ClientTokenConfig | null;
}> => {
  try {
    let authConfig = await axiosApiInstance.get<ClientTokenConfig>(
      getAuthConfigPath
    );
    if (authConfig.status === 200) {
      return {
        error: "",
        authConfig: authConfig.data,
      };
    } else {
      return {
        error: "Error inesperado",
        authConfig: null,
      };
    }
  } catch (e: any) {
    return {
      error: "Error inesperado",
      authConfig: null,
    };
  }
};

export const updateAuthConfigAPI = async (
  req: ClientTokenConfig
): Promise<{
  error: string;
  authConfig: ClientTokenConfig | null;
}> => {
  try {
    let authConfig = await axiosApiInstance.post<ClientTokenConfig>(
      updateAuthConfigPath,
      req
    );
    if (authConfig.status === 200) {
      return {
        error: "",
        authConfig: authConfig.data,
      };
    } else {
      return {
        error: "Error inesperado",
        authConfig: null,
      };
    }
  } catch (e: any) {
    return {
      error: "Error inesperado",
      authConfig: null,
    };
  }
};

export interface UserData {
  id: string;
  name: string;
  userName: string;
  email: string;
  roles: string[];
  phoneNumber: string;
  identification: string;
  userId?: string;
  active: boolean;
  provitionalPassword?: boolean;
  password?: string;
  generatedToken?: string;
  latestLogin?: string;
  creationDate?: string;
  roleProfileId?: string;
  changeHistory?: ChangeUser[];
  esAsesor?: boolean;
  lastUpdateDate?: string;
}

export interface ChangeUser {
  updateDate?: string;
  updatedById?: string;
  dataUpdated: { [key: string]: string };
  currentData: { [key: string]: string };
  description?: string;
}

export interface UserDataErrorMessage {
  nameError: string;
  emailError: string;
  phoneNumberError: string;
  identificationError: string;
}

export interface ChangeOwnPasswordReq {
  newPassword: string;
  oldPassword: string;
}

export interface PasswordData {
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

export interface PasswordDataError {
  oldPasswordError: string;
  newPasswordError: string;
  newPasswordRepeatError: string;
}

export interface PredeterminedRoles {
  adminRole: RoleInfo;
  containedRoles: RoleInfo[];
}

export interface RoleInfo {
  role: string;
  roleLabel: string;
}

export interface FilteredUsersRequest {
  page: number;
  pageSize?: number;
  creationDateStart?: string;
  creationDateEnd?: string;
  roleProfileId?: string;
  activeStatus?: "ACTIVE" | "SUSPENDED" | "ARCHIVED";
  esAsesor?: boolean;
  searchQuery?: string;
  sortType?: "CREATION_DATE" | "NAME" | "EMAIL";
  sortDesc?: boolean;
}

export interface FilteredUsersResponse {
  users: UserData[];
  numPages: number;
  currentPage: number;
}

export interface CreateUserRequest {
  userName: string;
  roles: string[];
  password?: string;
  name: string;
  email: string;
  userId: string;
  phoneNumber: string;
  identification: string;
  roleProfileId?: string;
  setAsesor?: boolean;
}

export interface AddMultipleUsersReq {
  users: CreateUserRequest[];
  forcedPassword?: string;
}

export interface CreateUserRequestError extends CreateUserRequest {
  motive?: string;
}

export interface AddMultipleUsersResponse {
  createdUsers: AddUserResult[];
  failedUsers: AddUserResult[];
  excelResult?: string;
}

export interface AddUserResult {
  username: string;
  password: string;
  errorMessage: string;
}
export interface ChangeInfoRequest {
  name: string;
  phoneNumber: string;
  identification: string;
  motive?: string;
  setAsesor?: boolean;
}

export interface ChangeUserRolesReq {
  userId: string;
  roles: string[];
  roleProfileId?: string;
}

export interface ResetBackofficePasswordResponse {
  generatedPassword: string;
}

export interface CreateUserResponse {
  userName: string;
  roles: string[];
  name: string;
  email: string;
  generatedPassword: string;
}

export interface GetFilteredEventsRequest {
  entityId: string | null;
  service: string | null;
  type: string | null;
  startedDate: string | null;
  finishedDate: string | null;
  userId: string | null;
  completed: boolean | null;
  page: number;
  pageSize?: number;
}

export interface FilteredEventsResponse {
  numPages: number;
  currentPage: number;
  events: EventData[];
  basicData: UserBasicData[];
}

export interface UserBasicData {
  entityId: string;
  name: string;
  document: string;
}

export interface EventData {
  id: string;
  name: string;
  entityId: string;
  startedDate: string;
  finishedDate: string | null;
  description: string;
  userId: string;
  cliendId: string;
  service:
    | "COREID"
    | "FIRMA"
    | "HYPERFLOW"
    | "SMARTFLOW"
    | "MAGICFORMS"
    | "PAGARES"
    | "CLIENTS"
    | "USERS"
    | "ACCOUNT"
    | "ALERTS"
    | null;
  type: string;
  currentState: string | null;
  previousState: string | null;
  completedAction: boolean;
  metadata: { [key: string]: string };
}

export interface RoleProfile {
  id?: string;
  clientId: string;
  name: string;
  description: string;
  roles: string[];
}

export interface ChangeMultipleUsersReq {
  userIds: string[];
}

export interface ClientTokenConfig {
  defaultTokenExpirationMinutes: number;
}
